import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSearchContent } from '../hooks/useContent';
import { useDebounce } from '../hooks/useDebounce';
import { useSearchHistory } from '../hooks/useSearchHistory';
import { ContentCard, PersonSummaryWithPhoto } from '../types';
import SearchBar from '../components/SearchBar';
import ContentGrid from '../components/ContentGrid';
import FilterPanel from '../components/FilterPanel';
import { navigateToContent, navigateToPerson, hasPersonDetailPage } from '../utils/navigation';

const SearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToHistory } = useSearchHistory();
  
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [selectedGenre, setSelectedGenre] = useState<string | undefined>();
  const [selectedYear, setSelectedYear] = useState<number | undefined>();
  const [page, setPage] = useState(1);
  const [allResults, setAllResults] = useState<ContentCard[]>([]);

  const debouncedQuery = useDebounce(query, 500);

  const { data: searchData, isLoading, error } = useSearchContent(
    debouncedQuery,
    page,
    20
  ) as { data: any; isLoading: boolean; error: any };

  // Update URL when query changes
  useEffect(() => {
    if (debouncedQuery) {
      setSearchParams({ q: debouncedQuery });
      addToHistory(debouncedQuery);
    } else {
      setSearchParams({});
    }
  }, [debouncedQuery, setSearchParams, addToHistory]);

  // Update results when data changes
  useEffect(() => {
    if (searchData?.data) {
      if (page === 1) {
        setAllResults(searchData.data);
      } else {
        setAllResults(prev => [...prev, ...searchData.data]);
      }
    }
  }, [searchData, page]);

  // Reset page when query changes
  useEffect(() => {
    setPage(1);
    setAllResults([]);
  }, [debouncedQuery]);

  // Initialize query from URL params
  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery && urlQuery !== query) {
      setQuery(urlQuery);
    }
  }, [searchParams]);

  const handleContentClick = (content: ContentCard) => {
    navigateToContent(navigate, content);
  };

  const handlePersonClick = (person: PersonSummaryWithPhoto) => {
    // Check if person has a detail page available
    if (hasPersonDetailPage(person)) {
      navigateToPerson(navigate, person);
    }
  };

  const handleLoadMore = useCallback(() => {
    if (searchData?.pagination?.hasMore && !isLoading) {
      setPage(prev => prev + 1);
    }
  }, [searchData?.pagination?.hasMore, isLoading]);

  const handleGenreChange = (genre: string | undefined) => {
    setSelectedGenre(genre);
    // TODO: Implement genre filtering for search results
  };

  const handleYearChange = (year: number | undefined) => {
    setSelectedYear(year);
    // TODO: Implement year filtering for search results
  };

  // Filter results based on selected filters
  const filteredResults = React.useMemo(() => {
    let filtered = allResults;

    if (selectedGenre) {
      filtered = filtered.filter(item => 
        item.genres.some(genre => 
          genre.toLowerCase().includes(selectedGenre.toLowerCase())
        )
      );
    }

    if (selectedYear) {
      filtered = filtered.filter(item => item.year === selectedYear);
    }

    return filtered;
  }, [allResults, selectedGenre, selectedYear]);

  return (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Search
          </h1>
          
          {/* Enhanced Search Bar */}
          <div className="max-w-2xl">
            <SearchBar 
              className="w-full"
              showSuggestions={true}
            />
          </div>
        </div>

        {/* Search Input for this page */}
        <div className="mb-8">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for movies, shows, actors..."
            className="w-full max-w-2xl bg-gray-800 text-white border border-gray-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
        </div>

        {/* Show current search query */}
        {debouncedQuery && (
          <div className="mb-6">
            <h2 className="text-xl text-white mb-4">
              Search results for "{debouncedQuery}"
            </h2>
            
            {/* Filters */}
            <FilterPanel
              selectedGenre={selectedGenre}
              selectedYear={selectedYear}
              onGenreChange={handleGenreChange}
              onYearChange={handleYearChange}
              className="mb-6"
            />
          </div>
        )}

        {/* Results Count */}
        {searchData?.pagination && debouncedQuery && (
          <div className="mb-6 text-gray-400">
            {filteredResults.length > 0 ? (
              <span>
                Showing {filteredResults.length} of {searchData.pagination.total} results
                {(selectedGenre || selectedYear) && ' (filtered)'}
              </span>
            ) : (
              <span>No results found</span>
            )}
          </div>
        )}

        {/* Content */}
        {!debouncedQuery ? (
          <div className="text-center py-16">
            <div className="text-gray-400 text-lg mb-4">
              Enter a search term to find movies and shows
            </div>
            <p className="text-gray-500">
              Search by title, genre, cast, or crew
            </p>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <div className="text-red-400 text-lg mb-4">
              Search failed
            </div>
            <button 
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        ) : (
          <ContentGrid
            content={filteredResults}
            onContentClick={handleContentClick}
            onPersonClick={handlePersonClick}
            onLoadMore={handleLoadMore}
            hasMore={searchData?.pagination?.hasMore && !selectedGenre && !selectedYear}
            isLoading={isLoading}
          />
        )}

        {/* No results message */}
        {debouncedQuery && !isLoading && filteredResults.length === 0 && !error && (
          <div className="text-center py-16">
            <div className="text-gray-400 text-lg mb-4">
              No results found for "{debouncedQuery}"
            </div>
            <div className="text-gray-500 space-y-2">
              <p>Try adjusting your search terms or filters</p>
              <p>• Check spelling</p>
              <p>• Use different keywords</p>
              <p>• Remove filters</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;